import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import EditIcon from '@/assets/icons/edit.svg';
import BeltIcon from '@/assets/images/icons/belt.png';
import { EditProfileHighlightsModal } from './EditProfileHighlightsModal';
import { useUpdateProfile } from '@/hooks/useUpdateProfile';
import { useToast } from '@/components/ui/use-toast';
import { EditLocationModal } from './EditLocationModal';
import { EditSocialModal } from './EditSocialModal';
import { EditProfessionModal } from './EditProfessionModal';
import { EditInterestsModal } from './EditInterestsModal';
import { EditFavoriteTechniquesModal } from './EditFavoriteTechniquesModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { PostCard } from '@/components/social/PostCard';
import { ConnectionsTab } from '@/components/connections/ConnectionsTab';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { UserConnectionsList } from '@/components/connections/UserConnectionsList';
import { ConnectionButton } from '@/components/connections/ConnectionButton';
import { useMyConnections } from '@/hooks/useMyConnections';
import { useUserConnections } from '@/hooks/useUserConnections';
import { InstructorsSection } from './InstructorsSection';
import { ProfileTotalsSlider } from './ProfileTotalsSlider';
import { MyJourneyTab } from './MyJourneyTab';
import { useNavigate } from 'react-router-dom';
import { useProfileInstructors } from '@/hooks/useProfileInstructors';
import { useI18n } from '@/contexts/I18nContext';
import { InstructorSlotDashboard } from './InstructorSlotDashboard';
import { useUserAgendaConfig } from '@/hooks/useUserAgendaConfig';

// Simplified prop types for the skeleton
interface ProfileHighlightsProps {
  fullName: string;
  username: string;
  belt: string;
  beltColor: string;
  academies: { id: number; name: string }[];
  location: { country: string; state?: string; city?: string };
  social: { instagram?: string; x?: string; facebook?: string; youtube?: string };
  website?: string;
  interests: string[];
  profession?: string;
  favoriteTechniques: Record<string, string[]>;
  techniqueCategories: string[];
  businessType?: string;
  linkedin?: string;
  editable?: boolean;
  email?: string;
  birthday?: string;
  profilePictureUrl?: string | null;
  onProfileUpdated?: () => void | Promise<void>;
  isOwnProfile?: boolean;
  userId?: string;
  isInstructor?: boolean;
}

const ProfileHighlights: React.FC<ProfileHighlightsProps> = (props) => {
  const {
    fullName,
    username,
    belt,
    beltColor,
    academies,
    location,
    social,
    website,
    interests,
    profession,
    favoriteTechniques,
    techniqueCategories,
    businessType,
    linkedin,
    editable = false,
    email,
    birthday,
    profilePictureUrl: initialProfilePictureUrl,
    onProfileUpdated,
    isOwnProfile = false,
    userId,
    isInstructor = false
  } = props;

  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(initialProfilePictureUrl || null);

  // Função para obter o nome de exibição da categoria
  const getCategoryDisplayName = (category: string) => {
    switch (category) {
      case 'Guard Types': return t('onboarding.techniques.categories.guard_types');
      case 'Guard Passing': return t('onboarding.techniques.categories.guard_passing');
      case 'Submissions': return t('onboarding.techniques.categories.submissions');
      case 'Sweeps & Takedowns': return t('onboarding.techniques.categories.sweeps_takedowns');
      case 'Escapes & Reversals': return t('onboarding.techniques.categories.escapes_reversals');
      default: return category;
    }
  };





  const initials = fullName ? fullName.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : 'U';
  const { t } = useI18n();
  
  // Get instructor agenda configuration for the specific user
  const { config: agendaConfig, isLoading: agendaLoading } = useUserAgendaConfig(userId);
  
  // Calculate total slots based on real agenda configuration
  const calculateTotalSlots = () => {
    if (!agendaConfig || !isInstructor) return 0;
    
    const workingDaysPerWeek = agendaConfig.working_days?.length || 0;
    const defaultDuration = agendaConfig.default_duration || 60;
    
    // Calculate slots per day based on working hours
    let totalSlots = 0;
    agendaConfig.working_days?.forEach(day => {
      const dayHours = agendaConfig.working_hours?.[day];
      if (dayHours) {
        const startTime = new Date(`2000-01-01T${dayHours.start}:00`);
        const endTime = new Date(`2000-01-01T${dayHours.end}:00`);
        const workingMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
        const slotsPerDay = Math.floor(workingMinutes / defaultDuration);
        totalSlots += slotsPerDay;
      }
    });
    
    return totalSlots;
  };
  
  const totalSlots = calculateTotalSlots();
  const slotsLoading = agendaLoading;

  // Location logic: if Brazil/USA, city | state - country; otherwise, just country
  let loc = '';
  let mainAcademy = '';
  
  if (location && location.country) {
    const countryLower = location.country.toLowerCase();
    
    if (countryLower === 'brazil' || countryLower === 'usa') {
      const city = location.city;
      const state = location.state ? location.state.toUpperCase() : undefined;
      
      if (city && state) {
        loc = `${city} | ${state} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
      } else if (city) {
        loc = `${city} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
      } else if (state) {
        const city = location.city;
        const state = location.state ? location.state.toUpperCase() : undefined;
        
        if (city && state) {
          loc = `${city} | ${state} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
        } else if (city) {
          loc = `${city} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
        } else if (state) {
          loc = `${state} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
        } else {
          loc = location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase();
        }
      } else {
        loc = location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase();
      }
    } else {
      loc = location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase();
    }
  }

  // Academy logic
  if (academies && academies.length > 0) {
    mainAcademy = academies[0].name;
  } else if (editable && isOwnProfile) {
    // Only show "Add your Jiu-Jitsu school" for own profile when editable
    mainAcademy = t('profile.add_your_jiu_jitsu_school');
  } else {
    // For other users without academy, show nothing
    mainAcademy = '';
  }
  const hasAcademy = !!academies[0];

  // Check if any category has techniques
  const hasAnyTechniques = techniqueCategories.some(cat => {
    const techniques = (favoriteTechniques && favoriteTechniques[cat]) ? favoriteTechniques[cat] : [];
    return techniques.length > 0;
  });

  const [activeTab, setActiveTab] = useState<'profile' | 'social' | 'instructors'>('profile');
  const [editOpen, setEditOpen] = useState(false);
  const updateProfile = useUpdateProfile();
  const { toast } = useToast();
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editSocialOpen, setEditSocialOpen] = useState(false);
  const [editProfessionOpen, setEditProfessionOpen] = useState(false);
  const [editInterestsOpen, setEditInterestsOpen] = useState(false);
  const [editFavoriteTechniquesOpen, setEditFavoriteTechniquesOpen] = useState(false);
  const { user } = useAuth();
  const [posts, setPosts] = useState<any[]>([]);
  const navigate = useNavigate();
  const [loadingPosts, setLoadingPosts] = useState(false);
  const { data: connectionsData, refetch } = useMyConnections();
  const connections = connectionsData?.pages?.flat() || [];
  const { connections: userConnections } = useUserConnections(userId);
  const { instructors } = useProfileInstructors(userId || '');
    
  // Check if user has instructors
  // Para o próprio usuário: sempre mostrar (mesmo sem instrutores)
  // Para outros usuários: mostrar apenas se tiverem instrutores
  const hasInstructors = isOwnProfile || (instructors && instructors.length > 0);

  const isEntrepreneur = (profession || '').trim().toLowerCase() === 'entrepreneur / business owner';

  // Utility function to build social links
  function getSocialLink(type: string, value?: string) {
    if (!value) return undefined;
    if (value.startsWith('http')) return value;
    switch (type) {
      case 'instagram':
        return `https://instagram.com/${value.replace(/^@/, '')}`;
      case 'x':
        return `https://x.com/${value.replace(/^@/, '')}`;
      case 'facebook':
        return `https://facebook.com/${value.replace(/^@/, '')}`;
      case 'youtube':
        return `https://youtube.com/@${value.replace(/^@/, '')}`;
      default:
        return value;
    }
  }

  // Prepare initial data for the modal
  const initialData = {
    fullName: fullName || '',
    username: username || '',
    beltRank: belt || 'white',
    academies: academies.map(a => ({ id: String(a.id), name: a.name })),
    email: email || '',
    birthday: birthday || '',
    isInstructor: isInstructor || false,
  };

  // Function to reload profile data (same as fetchData from MyJourney)
  const reloadProfile = async () => {
    if (!props.email) return;
    // Simulate fetchData from MyJourney
    const userId = updateProfile?.variables?.userId || null;
    if (!userId) return;
    // Ideally, you would call a function passed by prop or a context to reload the profile.
    // As a fallback, you can force a page reload:
    window.location.reload();
  };

  

  const handleSave = async (data: any) => {
    // Save data to backend
    try {
      const result = await updateProfile.mutateAsync({
        full_name: data.fullName,
        username: data.username,
        belt_rank: data.beltRank,
        main_academy_id: data.academies[0]?.id || null,
        is_instructor: data.isInstructor,
      });

      setEditOpen(false);
      // Reload profile data
      if (props.onProfileUpdated) await props.onProfileUpdated();
      toast({ title: 'Profile updated successfully!', variant: 'default' });
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      toast({
        title: 'Error updating profile',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  };

  // Function to save location
  const handleSaveLocation = async (data: { country: string; state: string; city: string }) => {
    try {
      await updateProfile.mutateAsync({
        country: data.country,
        state: data.state,
        city: data.city,
      });
      setEditLocationOpen(false);
      if (props.onProfileUpdated) await props.onProfileUpdated();
      toast({ title: 'Location updated successfully!', variant: 'default' });
    } catch (error: any) {
      toast({
        title: 'Error updating location',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  };

  // Function to save social networks
  const handleSaveSocial = async (data: { instagram: string; x: string; facebook: string; youtube: string; website: string; linkedin: string }) => {
    try {
      await updateProfile.mutateAsync({
        instagram: data.instagram,
        x: data.x,
        facebook: data.facebook,
        youtube: data.youtube,
        website: data.website,
        linkedin: data.linkedin,
      });
      setEditSocialOpen(false);
      if (props.onProfileUpdated) await props.onProfileUpdated();
      toast({ title: 'Social networks updated successfully!', variant: 'default' });
    } catch (error: any) {
      toast({
        title: 'Error updating social networks',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  };

  // Function to save profession
  const handleSaveProfession = async (data: { profession: string; businessType: string }) => {
    try {
      await updateProfile.mutateAsync({
        profession: data.profession,
        business_type: data.businessType,
      });
      setEditProfessionOpen(false);
      if (props.onProfileUpdated) await props.onProfileUpdated();
      toast({ title: 'Profession updated successfully!', variant: 'default' });
    } catch (error: any) {
      toast({
        title: 'Error updating profession',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  };

  // Function to save interests
  const handleSaveInterests = async (data: { interests: string[] }) => {
    try {
      if (!user?.id) throw new Error('User not authenticated');
      // Delete all old interests
      const { error: deleteError } = await supabase
        .from('profile_hobbies')
        .delete()
        .eq('profile_id', user.id);
      if (deleteError) throw deleteError;
      // Fetch hobby IDs by name
      const { data: hobbiesData, error: hobbiesError } = await supabase
        .from('hobbies')
        .select('id, name')
        .in('name', data.interests);
      if (hobbiesError) throw hobbiesError;
      if (hobbiesData && hobbiesData.length > 0) {
        const relationsToInsert = hobbiesData.map((hobby: any) => ({
          profile_id: user.id,
          hobby_id: hobby.id
        }));
        const { error: insertError } = await supabase
          .from('profile_hobbies')
          .insert(relationsToInsert);
        if (insertError) throw insertError;
      }
      setEditInterestsOpen(false);
      if (props.onProfileUpdated) await props.onProfileUpdated();
      toast({ title: 'Interests updated successfully!', variant: 'default' });
    } catch (error: any) {
      toast({
        title: 'Error updating interests',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  };

  // Function to save favorite techniques
  const handleSaveFavoriteTechniques = async (data: Record<string, string[]>) => {
    try {
      if (!user?.id) throw new Error('User not authenticated');
      // Delete all old techniques
      const { error: deleteError } = await supabase
        .from('profile_favorite_techniques')
        .delete()
        .eq('profile_id', user.id);
      if (deleteError) throw deleteError;
      // Normalize names before fetching from the database
      const techniqueNameMap = {
        'Closed Guard': 'Closed Guard',
        'Open Guard': 'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)',
        'Half Guard': 'Half Guard (e.g., Deep Half, Z-Guard, Knee Shield)',
        'Butterfly Guard': 'Butterfly Guard',
        'Worm Guard': 'Worm Guard',
        'Reverse De La Riva Guard': 'Reverse De La Riva Guard (RDLR)',
        '50/50 Guard': '50/50 Guard',
        'Sitting Guard': 'Sitting Guard',
        'No-Gi Specific Guards': 'No-Gi Specific Guards (e.g., Leg Pummeling, Ashi Garami)',
        'Pressure Passing': 'Pressure Passing (e.g., Knee Slice, Over Under, Stack Pass)',
        'Torque/Leg Drag Passing': 'Torque/Leg Drag Passing',
        'Leg Weave Passing': 'Leg Weave Passing',
        'Floating/Smash Passing': 'Floating/Smash Passing',
        'Movement-Based Passing': 'Movement-Based Passing (e.g., Toreando, Matador)',
        'Folding Passes': 'Folding Passes (e.g., Double Under)',
        'No-Gi Specific Passes': 'No-Gi Specific Passes (e.g., Head & Arm Pass, Darce Pass from Guard)',
        'Armlocks': 'Armlocks (e.g., Armbar, Kimura, Americana, Omoplata)',
        'Chokes': 'Chokes (e.g., Rear Naked Choke, Guillotine, Triangle, Cross Collar Choke, Darce, Anaconda, Bow and Arrow)',
        'Leg Locks': 'Leg Locks (e.g., Heel Hook, Kneebar, Ankle Lock, Calf Slicer)',
        'Shoulder Locks': 'Shoulder Locks (e.g., Wrist Lock, Omoplata, Americana)',
        'Spine/Neck Cranks': 'Spine/Neck Cranks (e.g., Twister)',
        'No-Gi Specific Submissions': 'No-Gi Specific Submissions (e.g., Head & Arm Choke, Ezekiel from Mount)',
        'Guard Sweeps': 'Guard Sweeps (e.g., Scissor Sweep, Hip Bump Sweep, Flower Sweep, Hook Sweeps)',
        'Half Guard Sweeps': 'Half Guard Sweeps (e.g., Old School Sweep, Waiter Sweep)',
        'Butterfly Sweeps': 'Butterfly Sweeps',
        'De La Riva Sweeps': 'De La Riva Sweeps',
        'Takedowns from Standing': 'Takedowns from Standing (e.g., Double Leg, Single Leg, Judo Throws, Body Lock)',
        'Counter Takedowns': 'Counter Takedowns',
        'Pulling Guard': 'Pulling Guard',
        'Mount Escapes': 'Mount Escapes (e.g., Upa, Elbow-Knee Escape)',
        'Back Escapes': 'Back Escapes (e.g., Bridge & Roll, Hip Escape)',
        'Side Control Escapes': 'Side Control Escapes (e.g., Hip Escape to Guard, Underhook Escapes)',
        'North South Escapes': 'North South Escapes',
        'Submission Escapes': 'Submission Escapes (e.g., Armbar Escape, Triangle Escape)',
        'Turtle Escapes': 'Turtle Escapes',
        'Half Guard Escapes': 'Half Guard Escapes',
      };
      const normalizedNames = Object.values(data).flat().map(name => techniqueNameMap[name] || name);
      if (normalizedNames.length > 0) {
        // Fetch technique IDs by name
        const { data: techniquesData, error: techniquesError } = await supabase
          .from('techniques')
          .select('id, name')
          .in('name', normalizedNames);
        if (techniquesError) throw techniquesError;
        if (techniquesData && techniquesData.length > 0) {
          // Insert new relations
          const relationsToInsert = techniquesData.map(tech => ({
            profile_id: user.id,
            technique_id: tech.id
          }));
          const { error: insertError } = await supabase
            .from('profile_favorite_techniques')
            .insert(relationsToInsert);
          if (insertError) throw insertError;
        }
      }
      setEditFavoriteTechniquesOpen(false);
      if (props.onProfileUpdated) await props.onProfileUpdated();
      toast({ title: 'Favorite techniques updated successfully!', variant: 'default' });
    } catch (error: any) {
      toast({
        title: 'Error updating favorite techniques',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    const targetUserId = isOwnProfile ? user?.id : props.userId;
    if (!targetUserId) return;
    const fetchPosts = async () => {
      setLoadingPosts(true);
      const { data: postData } = await supabase
        .from('posts')
        .select(`*, profiles:profiles!inner(full_name, belt_rank, profile_picture_url, username)`)
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(10);
      setPosts(
        (postData || []).map((post: any) => ({
          id: post.id,
          content: post.content,
          created_at: post.created_at,
          user_id: post.user_id,
          visibility: post.visibility,
          author: {
            full_name: post.profiles?.full_name || '',
            belt_rank: post.profiles?.belt_rank || 'white',
            profile_picture_url: post.profiles?.profile_picture_url || null,
            username: post.profiles?.username || null,
          },
        }))
      );
      setLoadingPosts(false);
    };
    fetchPosts();
  }, [isOwnProfile, props.userId, user?.id, editOpen, editSocialOpen, editInterestsOpen, editFavoriteTechniquesOpen]);

  return (
    <>
      <EditProfileHighlightsModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
        initialData={initialData}
      />
      <EditLocationModal
        open={editLocationOpen}
        onClose={() => setEditLocationOpen(false)}
        onSave={handleSaveLocation}
        initialData={{
          country: location?.country || '',
          state: location?.state || '',
          city: location?.city || '',
        }}
      />
      <EditSocialModal
        open={editSocialOpen}
        onClose={() => setEditSocialOpen(false)}
        onSave={handleSaveSocial}
        initialData={{
          instagram: social?.instagram || '',
          x: social?.x || '',
          facebook: social?.facebook || '',
          youtube: social?.youtube || '',
          website: website || '',
          linkedin: linkedin || '',
        }}
      />
      <EditProfessionModal
        open={editProfessionOpen}
        onClose={() => setEditProfessionOpen(false)}
        onSave={handleSaveProfession}
        initialData={{
          profession: profession || '',
          businessType: businessType || '',
        }}
      />
      <EditInterestsModal
        open={editInterestsOpen}
        onClose={() => setEditInterestsOpen(false)}
        onSave={handleSaveInterests}
        initialData={interests || []}
      />
      <EditFavoriteTechniquesModal
        open={editFavoriteTechniquesOpen}
        onClose={() => setEditFavoriteTechniquesOpen(false)}
        onSave={handleSaveFavoriteTechniques}
        initialData={favoriteTechniques || {}}
      />
      <div className="rounded-xl py-3 mt-2 mb-4 relative shadow-md overflow-x-hidden">
        {/* Personal data + edit icon aligned */}
        <div className="flex flex-row gap-2 mb-4 items-start w-full">
          <div className="flex gap-4 flex-1">
            <div className="relative w-[60px] h-[66px] flex-shrink-0 mt-0">
              <FramedAvatar
                src={profilePictureUrl || undefined}
                alt={fullName}
                initials={initials}
                frameSize={{ w: 60, h: 66 }}
                showFrame={true}
                beltRank={belt}
              />
              
              {/* Upload/Remove buttons for own profile */}
              {editable && isOwnProfile && (
                <ProfilePhotoUpload
                  currentPhotoUrl={profilePictureUrl}
                  onPhotoUpdate={(url) => {
                    setProfilePictureUrl(url);
                    onProfileUpdated?.();
                  }}
                  onError={(error) => console.error('Photo upload error:', error)}
                  size="sm"
                  className="absolute inset-0"
                />
              )}
            </div>
            <div className="flex flex-col justify-start min-w-0 flex-1">
              {/* Name and username on the same line */}
              <div className="flex flex-col min-w-0 max-w-full">
                <span className="text-lg font-bold text-white leading-tight truncate max-w-full font-roboto" style={{textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap'}}>{fullName || 'No Name'}</span>
                {username ? (
                  <span className="text-xs text-white/70 font-roboto break-all max-w-full font-normal">@{username}</span>
                ) : (
                  <span className="text-white/40 text-xs font-roboto italic font-normal">No username specified yet.</span>
                )}
              </div>
              {/* Main academy */}
              <div className="flex items-center gap-[4px] mt-1 w-full">
                {mainAcademy ? (
                  <span className="text-xs text-white/60 font-roboto font-normal">{mainAcademy}</span>
                ) : null}
                {academies.length > 1 && (
                  <span className="ml-0.5 text-xs font-roboto text-white/60 border border-white/60 rounded-full flex items-center justify-center" style={{width: 16, height: 16, minWidth: 16, minHeight: 16, lineHeight: '16px', padding: 0}}>
                    +{academies.length - 1}
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Edit icon aligned with highlights */}
          {editable && (
            <div className="flex flex-col justify-center h-full">
              <button
                className="text-white/60 hover:text-white cursor-pointer min-w-[20px] min-h-[20px] flex items-center justify-center"
                title={t('common.edit') + ' ' + t('profile.highlights').toLowerCase()}
                onClick={() => setEditOpen(true)}
                type="button"
              >
                <img src={EditIcon} alt={t('common.edit')} width={20} height={20} style={{ width: 20, height: 20 }} className="inline-block align-middle min-w-[20px] min-h-[20px]" />
              </button>
            </div>
          )}
        </div>
        {/* Profile Totals */}
        <ProfileTotalsSlider
          userId={userId || ''}
          isOwnProfile={isOwnProfile}
        />

        {/* Instructor Slot Dashboard - Show based on user type and privacy settings */}
        {userId && !slotsLoading && (() => {
          console.log('🔍 ProfileHighlights - Slot visibility check:', {
            userId,
            isInstructor,
            isOwnProfile,
            agendaConfig: agendaConfig ? { is_public: agendaConfig.is_public } : null,
            slotsLoading
          });
          
          // Rule 1: Praticante - never show
          if (!isInstructor) return false;
          
          // Rule 2: Own profile - show if instructor (regardless of privacy)
          if (isOwnProfile) return true;
          
          // Rule 3: Other profiles - show only if instructor + public agenda
          const shouldShow = agendaConfig?.is_public === true;
          console.log('🔍 ProfileHighlights - Should show slots:', shouldShow);
          return shouldShow;
        })() && (
          <>
            <InstructorSlotDashboard
              instructorId={userId}
              availableSlots={isOwnProfile ? totalSlots : (agendaConfig?.is_public ? totalSlots : 0)}
              isOwnProfile={isOwnProfile}
            />
          </>
        )}

        {/* Connection button for other users - above tabs */}
        {!isOwnProfile && userId && (
          <div className="mb-4 mt-6">
            <ConnectionButton targetUserId={userId} className="w-full" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-6 mb-2 border-b border-[#281B1B] w-full">
          <button onClick={() => setActiveTab('profile')} className={`flex-1 pb-2 px-0 text-sm font-semibold whitespace-nowrap min-w-0 ${activeTab === 'profile' ? 'text-white border-b-2 border-[#D72E16]' : 'text-white/60 border-b-2 border-transparent'}`}>{t('profile.about_me')}</button>
          <button onClick={() => setActiveTab('social')} className={`flex-1 pb-2 px-0 text-sm font-semibold whitespace-nowrap min-w-0 ${activeTab === 'social' ? 'text-white border-b-2 border-[#D72E16]' : 'text-white/60 border-b-2 border-transparent'}`}>{t('profile.journey')}</button>
          {hasInstructors && (
            <button onClick={() => setActiveTab('instructors')} className={`flex-1 pb-2 px-0 text-sm font-semibold whitespace-nowrap min-w-0 ${activeTab === 'instructors' ? 'text-white border-b-2 border-[#D72E16]' : 'text-white/60 border-b-2 border-transparent'}`}>{t('profile.instructor')}</button>
          )}
        </div>
        {/* Tab content */}
        {activeTab === 'profile' && (
          <div>
            {/* Location */}
            <div className="flex justify-between mt-6 mb-3">
              <div className={`text-sm ${loc ? 'text-white' : 'text-white/60'}`}>
                {loc || (isOwnProfile ? t('profile.add_your_location') : t('profile.no_location_added'))}
              </div>
              <div className="">
                {editable && (
                  <button className="text-white/60 hover:text-white p-1 min-w-[20px] min-h-[20px] flex items-center justify-center" title={t('common.edit') + ' ' + t('profile.location').toLowerCase()} onClick={() => setEditLocationOpen(true)}>
                    <img src={EditIcon} alt={t('common.edit')} width={20} height={20} style={{ width: 20, height: 20 }} className="inline-block align-middle min-w-[20px] min-h-[20px]" />
                  </button>
                )}
              </div>
            </div>

            {/* Social Links */}
            <div className="flex justify-between mb-3">
              <div className="flex flex-wrap gap-2">
                {social.instagram && (
                  <a href={getSocialLink('instagram', social.instagram)} target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 text-xs font-poppins font-medium underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    Instagram
                  </a>
                )}
                {social.x && (
                  <a href={getSocialLink('x', social.x)} target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 text-xs font-poppins font-medium underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    X
                  </a>
                )}
                {social.facebook && (
                  <a href={getSocialLink('facebook', social.facebook)} target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 text-xs font-poppins font-medium underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    Facebook
                  </a>
                )}
                {social.youtube && (
                  <a href={getSocialLink('youtube', social.youtube)} target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 text-xs font-poppins font-medium underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    YouTube
                  </a>
                )}
                {website && (
                  <a href={website} target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 text-xs font-poppins font-medium underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    Website
                  </a>
                )}
                {linkedin && (
                  <a href={linkedin} target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 text-xs font-poppins font-medium underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    LinkedIn
                  </a>
                )}
                {!social.instagram && !social.x && !social.facebook && !social.youtube && !website && !linkedin && (
                  <span className={`text-sm ${isOwnProfile ? 'text-white/60' : 'text-white/60'}`}>
                    {isOwnProfile ? 'Add your social links' : t('calendar.no_social_links_added')}
                  </span>
                )}
              </div>
              <div className="flex justify-end">
                {editable && (
                  <button className="text-white/60 hover:text-white p-1 min-w-[20px] min-h-[20px] flex items-center justify-center" title={t('common.edit') + ' ' + t('profile.social_networks').toLowerCase()} onClick={() => setEditSocialOpen(true)}>
                    <img src={EditIcon} alt={t('common.edit')} width={20} height={20} style={{ width: 20, height: 20 }} className="inline-block align-middle min-w-[20px] min-h-[20px]" />
                  </button>
                )}
              </div>
            </div>

            {/* Profession */}
            <div className="flex justify-between mb-3">
              <div className={`text-sm ${profession ? 'text-white' : 'text-white/60'}`}>
                {profession || (isOwnProfile ? t('profile.add_your_profession') : t('profile.no_profession_added'))}
              </div>
              <div className="flex">
                {editable && (
                  <button className="text-white/60 hover:text-white p-1 min-w-[20px] min-h-[20px] flex items-center justify-center" title={t('common.edit') + ' ' + t('profile.profession').toLowerCase()} onClick={() => setEditProfessionOpen(true)}>
                    <img src={EditIcon} alt={t('common.edit')} width={20} height={20} style={{ width: 20, height: 20 }} className="inline-block align-middle min-w-[20px] min-h-[20px]" />
                  </button>
                )}
              </div>
            </div>

            {/* Interests */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-white">{t('profile.interests')}</h3>
                {editable && (
                  <button className="text-white/60 hover:text-white p-1 min-w-[20px] min-h-[20px] flex items-center justify-center" title={t('common.edit') + ' ' + t('profile.interests').toLowerCase()} onClick={() => setEditInterestsOpen(true)}>
                    <img src={EditIcon} alt={t('common.edit')} width={20} height={20} style={{ width: 20, height: 20 }} className="inline-block align-middle min-w-[20px] min-h-[20px]" />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {interests.length > 0 ? (
                  interests.map((interest) => (
                    <span key={interest} className="px-3 py-0 bg-[#281B1B] text-white rounded-full text-[10px] border border-white font-poppins font-medium">
                      {interest}
                    </span>
                  ))
                ) : (
                  <span className={`text-sm ${interests.length > 0 ? 'text-white' : 'text-white/60'}`}>
                    {isOwnProfile ? t('profile.add_your_interests') : t('profile.no_interests_added')}
                  </span>
                )}
              </div>
            </div>

            {/* Favorite Techniques */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-white">{t('profile.favorite_techniques')}</h3>
                {editable && (
                  <button className="text-white/60 hover:text-white p-1 min-w-[20px] min-h-[20px] flex items-center justify-center" title={t('common.edit') + ' ' + t('profile.favorite_techniques').toLowerCase()} onClick={() => setEditFavoriteTechniquesOpen(true)}>
                    <img src={EditIcon} alt={t('common.edit')} width={20} height={20} style={{ width: 20, height: 20 }} className="inline-block align-middle min-w-[20px] min-h-[20px]" />
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {hasAnyTechniques ? (
                  Object.entries(favoriteTechniques)
                    .filter(([category, techniques]) => techniques && techniques.length > 0)
                    .map(([category, techniques]) => (
                      <div key={category} className="space-y-2">
                        <h4 className="text-xs font-medium text-white/80 tracking-wide">
                          {getCategoryDisplayName(category)}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {techniques.map((technique) => (
                            <span key={technique} className="px-3 py-0 bg-[#281B1B] text-white rounded-full text-[10px] border border-white font-poppins font-medium">
                              {technique}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className={`text-sm ${hasAnyTechniques ? 'text-white' : 'text-white/60'}`}>
                    {isOwnProfile ? t('profile.add_your_favorite_techniques') : t('profile.no_favorite_techniques_added')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {activeTab === 'social' && (
          <div>
            <MyJourneyTab 
              userId={userId || ''} 
              isOwnProfile={isOwnProfile} 
            />
          </div>
        )}
        {activeTab === 'instructors' && (
          <div className="w-full">
            {/* Instructors content */}
            <div className="mb-6 w-full">
              <InstructorsSection 
                profileId={userId || ''} 
                isOwnProfile={isOwnProfile} 
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileHighlights; 